// Synthetic data for the unified customer timeline demo.
// Brightleaf is a made-up supplement subscription brand. No real customers or companies.

export type Source = 'Ads' | 'Site' | 'Shopify' | 'Email' | 'SMS' | 'Subscription' | 'Support';

export const SOURCES: { key: Source; label: string; system: string; badge: string; dot: string }[] = [
  { key: 'Ads', label: 'Ads', system: 'Meta, Google, TikTok', badge: 'bg-[#ffe8c7] text-[#7a3d00]', dot: 'bg-[#b45309]' },
  { key: 'Site', label: 'Site', system: 'GA4 or server events with UTMs', badge: 'bg-slate-200 text-slate-700', dot: 'bg-slate-600' },
  { key: 'Shopify', label: 'Orders', system: 'Shopify', badge: 'bg-[#e6f4ec] text-[#14532d]', dot: 'bg-[#15803d]' },
  { key: 'Email', label: 'Email', system: 'Klaviyo', badge: 'bg-[#dbe8fb] text-[#0f3f80]', dot: 'bg-[#1a4e94]' },
  { key: 'SMS', label: 'SMS', system: 'Klaviyo SMS', badge: 'bg-sky-100 text-sky-900', dot: 'bg-sky-700' },
  { key: 'Subscription', label: 'Subscription', system: 'Recharge', badge: 'bg-[#ece4fb] text-[#4a2a91]', dot: 'bg-[#6d28d9]' },
  { key: 'Support', label: 'Support', system: 'Gorgias', badge: 'bg-rose-100 text-rose-900', dot: 'bg-rose-700' },
];

export interface TimelineEvent {
  date: string; // ISO date
  source: Source;
  title: string;
  detail: string;
}

export interface Customer {
  id: string;
  name: string;
  tag: string;
  email: string;
  status: 'Active subscriber' | 'Lapsed' | 'New';
  channel: string;
  ids: { system: string; id: string }[];
  events: TimelineEvent[];
}

export const CUSTOMERS: Customer[] = [
  {
    id: 'maya',
    name: 'Maya R.',
    tag: 'Subscriber for 14 months',
    email: 'maya.r@example.com',
    status: 'Active subscriber',
    channel: 'Meta',
    ids: [
      { system: 'Shopify customer ID', id: '7104382911' },
      { system: 'Klaviyo profile ID', id: '01HQ7XK2M9' },
      { system: 'Recharge customer ID', id: '4821907' },
      { system: 'First-touch UTM', id: 'utm_source=meta' },
    ],
    events: [
      { date: '2025-09-16', source: 'Ads', title: 'Clicked a Meta ad', detail: 'Fall launch video' },
      { date: '2025-09-18', source: 'Site', title: 'Landed on the product page', detail: 'utm_campaign=fall_launch' },
      { date: '2025-09-18', source: 'Shopify', title: 'First order #8812', detail: '$59.00, started a subscription' },
      { date: '2025-09-18', source: 'Subscription', title: 'Subscription created', detail: 'Every 30 days' },
      { date: '2025-09-19', source: 'Email', title: 'Opened "Welcome to Brightleaf"', detail: 'Welcome flow' },
      { date: '2025-10-18', source: 'Shopify', title: 'Order #8990', detail: '$54.00, subscription renewal' },
      { date: '2025-11-17', source: 'Shopify', title: 'Order #9104', detail: '$54.00, subscription renewal' },
      { date: '2025-12-17', source: 'Shopify', title: 'Order #9236', detail: '$54.00, subscription renewal' },
      { date: '2026-01-16', source: 'Shopify', title: 'Order #9351', detail: '$54.00, subscription renewal' },
      { date: '2026-02-15', source: 'Shopify', title: 'Order #9478', detail: '$54.00, subscription renewal' },
      { date: '2026-03-04', source: 'Subscription', title: 'Changed frequency', detail: 'Every 30 days to every 45 days' },
      { date: '2026-04-01', source: 'Shopify', title: 'Order #9602', detail: '$54.00, subscription renewal' },
      { date: '2026-05-16', source: 'Shopify', title: 'Order #9745', detail: '$54.00, subscription renewal' },
      { date: '2026-06-30', source: 'Shopify', title: 'Order #9893', detail: '$54.00, subscription renewal' },
      { date: '2026-08-14', source: 'Shopify', title: 'Order #10031', detail: '$54.00, subscription renewal' },
      { date: '2026-09-15', source: 'Support', title: 'Ticket solved', detail: 'Updated delivery address' },
      { date: '2026-09-25', source: 'SMS', title: 'Clicked "Ships in 3 days"', detail: 'Shipping reminder' },
      { date: '2026-09-28', source: 'Shopify', title: 'Order #10187', detail: '$54.00, subscription renewal' },
      { date: '2026-10-21', source: 'Email', title: 'Opened "Time to refill?"', detail: 'Replenishment flow' },
      { date: '2026-10-28', source: 'Subscription', title: 'Skipped next shipment', detail: 'Reason given: too much product' },
    ],
  },
  {
    id: 'jordan',
    name: 'Jordan T.',
    tag: 'Lapsed, last order in June',
    email: 'jordan.t@example.com',
    status: 'Lapsed',
    channel: 'Google',
    ids: [
      { system: 'Shopify customer ID', id: '8829017734' },
      { system: 'Klaviyo profile ID', id: '01J2C8RT4P' },
      { system: 'Recharge customer ID', id: 'None, one-time buyer' },
      { system: 'First-touch UTM', id: 'utm_source=google' },
    ],
    events: [
      { date: '2026-02-20', source: 'Ads', title: 'Clicked a Google Shopping ad', detail: 'Brand search' },
      { date: '2026-02-20', source: 'Shopify', title: 'First order #8977', detail: '$57.00, one-time purchase' },
      { date: '2026-02-21', source: 'Email', title: 'Received "Subscribe and save 10%"', detail: 'Post-purchase flow' },
      { date: '2026-04-10', source: 'Shopify', title: 'Order #9420', detail: '$57.00, one-time purchase' },
      { date: '2026-04-11', source: 'Email', title: 'Clicked "Bundle and save"', detail: 'Campaign' },
      { date: '2026-06-04', source: 'Shopify', title: 'Order #9901', detail: '$57.00, one-time purchase' },
      { date: '2026-08-15', source: 'Email', title: 'Received "We miss you"', detail: 'Win-back flow, no open' },
      { date: '2026-10-30', source: 'SMS', title: 'Received a win-back offer', detail: 'Not clicked yet' },
    ],
  },
  {
    id: 'sam',
    name: 'Sam K.',
    tag: 'New, first order last week',
    email: 'sam.k@example.com',
    status: 'New',
    channel: 'TikTok',
    ids: [
      { system: 'Shopify customer ID', id: '9311200458' },
      { system: 'Klaviyo profile ID', id: '01JB8W6Q2D' },
      { system: 'Recharge customer ID', id: '9932118' },
      { system: 'First-touch UTM', id: 'utm_source=tiktok' },
    ],
    events: [
      { date: '2026-10-26', source: 'Ads', title: 'Watched a TikTok ad', detail: 'Creator video' },
      { date: '2026-10-27', source: 'Site', title: 'Took the product quiz', detail: 'utm_source=tiktok' },
      { date: '2026-10-27', source: 'Shopify', title: 'First order #10520', detail: '$59.00, started a subscription' },
      { date: '2026-10-27', source: 'Subscription', title: 'Subscription created', detail: 'Every 30 days' },
      { date: '2026-10-29', source: 'Email', title: 'Opened "Welcome to Brightleaf"', detail: 'Welcome flow' },
    ],
  },
];

export function summarize(customer: Customer) {
  const orders = customer.events.filter((e) => e.source === 'Shopify');
  const revenue = orders.reduce((sum, e) => {
    const match = /\$([\d,]+\.\d{2})/.exec(e.detail);
    return sum + (match ? Number(match[1].replace(/,/g, '')) : 0);
  }, 0);
  const first = orders.map((e) => e.date).sort()[0] ?? null;
  return { orders: orders.length, revenue, firstOrder: first };
}

export function sortedEvents(customer: Customer, hidden: Source[] = []) {
  return customer.events
    .filter((e) => !hidden.includes(e.source))
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export function formatDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1];
  return `${month} ${d}, ${y}`;
}
